<template>
  <qtm-dialog-card
    v-if="quote"
    v-bind="$attrs"
    header-background="white"
    closable
    color="white"
    fullscreen
    hide-overlay
    no-click-animation
    persistent
    title-class="qtm-border-bottom"
    transition="dialog-bottom-transition"
    @update:model-value="close"
  >
    <template v-slot:title>
      <qtm-btn class="px-0" prepend-icon="mdi-chevron-left" tertiary @click="close">
        {{ order.reference_name }}
      </qtm-btn>
    </template>

    <template v-slot:title-center>
      <quote-progress model-value="details" style="justify-self: center;" />
    </template>

    <div class="d-flex non-scrollable-container">
      <div v-if="showAttachment" class="attachment-viewer ml-4 mt-4 mr-2">
        <attachment-viewer
          ref="pdfViewer"
          scrollable
          select-text-layer
          :src="documentToPreviewSrc"
          :name="documentToPreviewName"
          @close="showAttachment = false"
        />
      </div>
      <project-information v-else class="project-information" :order="order" />
      <div class="flex-grow-1 scrollable-container pt-4">
        <quote-form
          ref="quoteForm"
          v-model:show-match-items-dialog="showMatchItemsDialog"
          change-supplier
          class="quote-form"
          :items-are-matched="itemsAreMatched"
          :order="order"
          :quote="quote"
          @preview="showDocumentPreview($event)"
        >
          <v-row class="pa-5" justify="end">
            <qtm-btn :loading="loading" tertiary size="x-large" @click="close">
              Cancel
            </qtm-btn>
            <qtm-btn class="mx-3" :loading="loading" secondary size="x-large" @click="save">
              Save
            </qtm-btn>
            <qtm-btn
              v-if="!order.orderType?.fromBom || itemsAreMatched"
              :loading="loading"
              size="x-large"
              @click="save({ closeDialog: true })"
            >
              Save and Close
            </qtm-btn>
            <qtm-btn v-else :loading="loading" size="x-large" @click="showMatchItemsDialog = true">
              Match items
            </qtm-btn>
          </v-row>
        </quote-form>
      </div>
    </div>
  </qtm-dialog-card>
</template>

<script setup lang="ts">
import type { Order, Quote } from '@quotetome/materials-api'
import AttachmentViewer from '@/components/attachments/attachment-viewer.vue'
import ProjectInformation from '@/components/admin/project-information.vue'
import QuoteForm from '@/components/quotes/quote-form.vue'
import QuoteProgress from '@/components/quotes/quote-progress.vue'

export interface Props {
  order: Order
  quote?: Quote
}
const props = defineProps<Props>()

const emit = defineEmits(['update:model-value'])

const adminStore = useAdminStore()

const documentToPreviewSrc = ref()
const documentToPreviewName = ref()
const loading = ref(false)
const showAttachment = ref(false)
const showMatchItemsDialog = ref(false)

const itemsAreMatched = computed(() => props.quote?.skus.some(item => item.reference_identifier))

const close = () => {
  showAttachment.value = false
  emit('update:model-value', false)
}

const { $api, $error, $toast } = useNuxtApp()
const refQuoteForm = useTemplateRef('quoteForm')
const isValid = async () => {
  refQuoteForm.value?.clear()
  await nextTick()

  const valid = refQuoteForm.value?.isValid()

  if (!valid) {
    $toast.error('Please correct the errors')
  }

  return valid
}

const save = async ({ closeDialog = false } = {}) => {
  if (!(await isValid())) {
    return
  }

  const quoteData = { ...props.quote?.getSubmitData(), outdated: props.quote?.outdated }

  loading.value = true
  try {
    await $api.v1.quotes.patch(props.quote?.id, quoteData)
    const updatedOrder = await $api.v1.rfqs.get(props.order.id)
    $toast.success('Quote saved')
    adminStore.updateOrder(updatedOrder)

    if (closeDialog) {
      close()
    }
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

const showDocumentPreview = (attachment: any) => {
  documentToPreviewName.value = attachment.name
  documentToPreviewSrc.value = attachment.url
  showAttachment.value = true
}
</script>

<style lang="scss" scoped>
.attachment-viewer {
  flex-shrink: 0;
  width:48%;
}

.non-scrollable-container {
  height: 100%;
  overflow-y: hidden;
}

.project-information {
  flex-shrink: 0;
  margin: 1rem 0 0 0;
  max-height: calc(100vh - 5rem);
  max-width: 20rem;
  overflow-y: auto;
  padding: 5px 10px;
}

.quote-form {
  max-width: calc(100vw - 22rem);
}

.scrollable-container {
  overflow-y: auto;
}
</style>
